import React, { useContext, useEffect, useState } from 'react';
import { Button, Col, Container, Form, Row } from 'react-bootstrap';
import { Helmet } from 'react-helmet-async';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from "react-router-dom";

import BusinessUnitContext from '../../../contexts/BusinessUnitAccessContext';
import PrivilegesContext from '../../../contexts/PrivilegesContext';
import { DateToLocaleString, longDateAndTimeFormat } from '../../../utilities/DateUtils';
import CentredSpinner from '../../CentredSpinner';
import ChangeHistory from './ChangeHistory';
import QIRAssignment from './QIRAssignment';
import QIRAttachment from './QIRAttachment';
import QIRComments from './QIRComments';
import QIRDetails from './QIRDetails';
import QIRSummary from './QIRSummary';
import GradingActionsSection from './GradingActionsSection';
import AddCommentDialog from '../../Dialogs/AddCommentDialog';

function QIRComponent({ qir, setQir, action, submitButtonText, onSave, refresh }) {

    const { t } = useTranslation();
    const [searchParams] = useSearchParams();
    const businessUnitQueryParam = searchParams.get('businessUnit');
    const { supervisor, admin } = useContext(PrivilegesContext);
    const [validated, setValidated] = useState(false);
    const { getBusinessUnitDetails } = useContext(BusinessUnitContext);

    const [businessUnitDetails, setBusinessUnitDetails] = useState();
    useEffect(() => {
        if (qir.businessUnitId) {
            setBusinessUnitDetails(getBusinessUnitDetails(qir.businessUnitId));
        }
    }, [getBusinessUnitDetails, qir.businessUnitId])

    const onChange = (e) => {
        let field = e.currentTarget.name;
        let value = e.currentTarget.value;

        if (typeof qir[field] === 'boolean') {
            value = value === 'true';
        }

        setQir({
            ...qir,
            [field]: value
        });
    };

    const handleSave = (e) => {
        e.preventDefault();
        e.stopPropagation();

        const form = e.currentTarget;

        if (form.checkValidity() === true) {
            if (action === "create") {
                onSave();
            } else {
                setShowCommentDialog(true);
            }
        }

        setValidated(true);
    };

    const [showCommentDialog, setShowCommentDialog] = useState(false);
    const [formDisabled, setFormDisabled] = useState(false);

    // Display a placeholder for further QIR sections depending on if we need user interaction or are loading in required data
    let qirComponentPlaceholder;
    if (!qir.businessUnitId && !businessUnitQueryParam) {
        qirComponentPlaceholder = <h4 className="d-flex justify-content-center">{t("QIRDetail.SelectBusinessUnit")}</h4>
    } else if (!businessUnitDetails) {
        qirComponentPlaceholder = <CentredSpinner />
    }

    return (
        <>
            {action !== "create" &&
                <Helmet>
                    <title>{t('QIRDetail.TabTitle') + " " + qir.id}</title>
                </Helmet>
            }
            <Container>
                <Form noValidate validated={validated} onSubmit={handleSave} id="createQIR" className="md-5" disabled={formDisabled && !admin && !supervisor}>

                    <QIRSummary
                        action={action}
                        qir={qir}
                        setQir={setQir}
                        onChange={onChange}
                        formDisabled={formDisabled}
                        setFormDisabled={setFormDisabled}
                        businessUnitDetails={businessUnitDetails} />
                    {qirComponentPlaceholder
                        ? qirComponentPlaceholder
                        : <>
                            <fieldset id="qir-fieldset" disabled={formDisabled}>
                                <QIRDetails
                                    action={action}
                                    qir={qir}
                                    onChange={onChange}
                                    selectedBusinessUnitId={qir.businessUnitId}
                                    businessUnitDetails={businessUnitDetails} />

                                {action !== "create" &&
                                    <QIRComments qir={qir} businessUnitDetails={businessUnitDetails} refresh={refresh} />
                                }

                                {businessUnitDetails.showGradingActionsSection &&
                                    <GradingActionsSection qir={qir} onChange={onChange} businessUnitDetails={businessUnitDetails} />
                                }

                                <QIRAssignment businessUnitId={qir.businessUnitId}
                                    controlId="assignedTo"
                                    controlName="assignedTo"
                                    label={t('QIRDetail.Fields.AssignedTo')}
                                    value={qir.assignedTo && qir.assignedTo}
                                    onChange={onChange}
                                    disabled={action === "view"}
                                    businessUnitDetails={businessUnitDetails} />

                                <QIRAttachment qir={qir} onChange={onChange} />

                                {action !== "create" &&
                                    <ChangeHistory qir={qir} />
                                }
                            </fieldset>
                            <Row>
                                <Col md={4}>
                                    {action !== "create" && !qir.modifiedDate &&
                                        <Form.Group as={Row} className="mb-1" controlId="createdDate">
                                            <Form.Label column sm={3}>{t('QIRDetail.Fields.CreatedDate')}</Form.Label>
                                            <Col sm={9}>
                                                <Form.Control type="text" size="sm" value={qir.createdDate && DateToLocaleString(qir.createdDate, longDateAndTimeFormat)} name="createdDate" onChange={onChange} readOnly />
                                            </Col>
                                        </Form.Group>
                                    }
                                    {action !== "create" && qir.modifiedDate &&
                                        <Form.Group as={Row} className="mb-1" controlId="modifiedDate">
                                            <Form.Label column sm={3}>{t('QIRDetail.Fields.ModifiedDate')}</Form.Label>
                                            <Col sm={9}>
                                                <Form.Control type="text" size="sm" value={qir.modifiedDate && DateToLocaleString(qir.modifiedDate, longDateAndTimeFormat)} name="modifiedDate" onChange={onChange} readOnly />
                                            </Col>
                                        </Form.Group>
                                    }
                                </Col>
                                <Col md={4}>
                                    {action !== "create" && !qir.modifiedBy &&
                                        <Form.Group as={Row} className="mb-1" controlId="createdBy">
                                            <Form.Label column sm={3}>{t('QIRDetail.Fields.CreatedBy')}</Form.Label>
                                            <Col sm={9}>
                                                <Form.Control type="text" size="sm" value={qir.createdBy && qir.createdBy} name="createdBy" onChange={onChange} readOnly />
                                            </Col>
                                        </Form.Group>
                                    }
                                    {action !== "create" && qir.modifiedBy &&
                                        <Form.Group as={Row} className="mb-1" controlId="modifiedBy">
                                            <Form.Label column sm={3}>{t('QIRDetail.Fields.ModifiedBy')}</Form.Label>
                                            <Col sm={9}>
                                                <Form.Control type="text" size="sm" value={qir.modifiedBy && qir.modifiedBy} name="modifiedBy" onChange={onChange} readOnly />
                                            </Col>
                                        </Form.Group>
                                    }
                                </Col>
                                <Col md={4}>
                                    {(action === "create" || action === "update") &&
                                        <Button type="submit" className="w-100 mb-3" disabled={formDisabled && !admin && !supervisor}>
                                            {submitButtonText}
                                        </Button>
                                    }
                                </Col>
                            </Row>
                        </>
                    }
                </Form>
                <AddCommentDialog showDialog={showCommentDialog} closeDialog={() => setShowCommentDialog(false)} businessUnitDetails={businessUnitDetails} onSave={onSave} />
            </Container>
        </>
    );
}

export default QIRComponent;